// src/components/answer/AnswerSources.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * AnswerSources
 *
 * Renders clean clickable sources:
 * - no raw URL visible
 * - numbered source rows
 * - first 5 visible by default
 * - Show 5 more button
 * - same source numbering as inline citation markers
 *
 * Safety / correctness:
 * - preserve valid citation IDs exactly
 * - do not dedupe different citation IDs merely because URLs match
 * - reject non-http(s) URLs from clickable rendering
 * - preserve useful static labels when a URL is invalid/missing
 * - never expose the raw URL as visible label/title text
 */

const MAX_CITATION_ID = 999;

function normalizeBatchSize(value) {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return 5;
  }

  return Math.min(
    50,
    Math.max(
      1,
      Math.floor(parsed)
    )
  );
}

function safeUrl(url) {
  const raw =
    String(url ?? "").trim();

  if (
    !raw ||
    /[\u0000-\u001F\u007F]/.test(raw) ||
    !/^https?:\/\//i.test(raw)
  ) {
    return "";
  }

  try {
    const parsed =
      new URL(raw);

    if (
      ![
        "http:",
        "https:",
      ].includes(
        parsed.protocol
      ) ||
      !parsed.hostname
    ) {
      return "";
    }

    return parsed.toString();
  } catch {
    return "";
  }
}

function hostnameLabel(url) {
  if (!url) return "";

  try {
    return new URL(url)
      .hostname
      .replace(/^www\./i, "")
      .trim();
  } catch {
    return "";
  }
}

function cleanLabel(label, url) {
  let out =
    String(label ?? "").trim();

  /*
   * Preserve the human-readable part of markdown links:
   * [IRS guidance](https://irs.gov/...) -> IRS guidance
   */
  out = out.replace(
    /\[\s*([^\]]+?)\s*\]\(\s*https?:\/\/[^)]*\)/gi,
    "$1"
  );

  /*
   * Remove remaining raw URLs from visible labels.
   * The actual validated URL remains in href only.
   */
  out = out.replace(
    /https?:\/\/[^\s<>)\]]+/gi,
    ""
  );

  out = out.replace(
    /^\s*[-*•]\s+/,
    ""
  );

  out = out.replace(
    /^\s*\[\d{1,3}\]\s*/,
    ""
  );

  out = out
    .replace(/\s+/g, " ")
    .replace(/[.;,\s]+$/g, "")
    .trim();

  if (!out) {
    out =
      hostnameLabel(url) ||
      "Source";
  }

  return out;
}

function parseCitationId(value) {
  const raw =
    String(value ?? "").trim();

  if (!/^\d{1,3}$/.test(raw)) {
    return null;
  }

  const id = Number(raw);

  if (
    !Number.isInteger(id) ||
    id < 1 ||
    id > MAX_CITATION_ID
  ) {
    return null;
  }

  return id;
}

function nextAvailableCitationId(
  usedIds,
  preferredStart = 1
) {
  let candidate =
    Math.max(
      1,
      Math.floor(
        preferredStart
      ) || 1
    );

  while (
    usedIds.has(candidate) &&
    candidate <= MAX_CITATION_ID
  ) {
    candidate += 1;
  }

  if (
    candidate <=
    MAX_CITATION_ID
  ) {
    return candidate;
  }

  for (
    let id = 1;
    id <= MAX_CITATION_ID;
    id += 1
  ) {
    if (!usedIds.has(id)) {
      return id;
    }
  }

  return null;
}

function sourceQuality(source) {
  let score = 0;

  if (source.url) {
    score += 4;
  }

  if (
    source.label &&
    source.label !== "Source"
  ) {
    score += 2;
  }

  if (
    source.label &&
    source.url &&
    source.label !==
      hostnameLabel(source.url)
  ) {
    score += 1;
  }

  return score;
}

function normalizeSources(sources) {
  const input =
    Array.isArray(sources)
      ? sources
      : [];

  const usedIds =
    new Set();

  const normalized = [];

  const explicitById =
    new Map();

  const pending = [];

  /*
   * Pass 1:
   * Preserve every valid explicit citation ID exactly.
   *
   * If the same citation ID is repeated, keep the stronger representation for
   * that same citation slot rather than rendering two rows with the same [n].
   */
  input.forEach(
    (source, index) => {
      const explicitId =
        parseCitationId(
          source?.id
        );

      const url =
        safeUrl(
          source?.url
        );

      const label =
        cleanLabel(
          source?.label,
          url
        );

      const candidate = {
        id: explicitId,
        label,
        url,
        originalIndex:
          index,
      };

      if (
        explicitId === null
      ) {
        pending.push(
          candidate
        );
        return;
      }

      const existing =
        explicitById.get(
          explicitId
        );

      if (
        !existing ||
        sourceQuality(
          candidate
        ) >
          sourceQuality(
            existing
          )
      ) {
        explicitById.set(
          explicitId,
          candidate
        );
      }
    }
  );

  [
    ...explicitById.values(),
  ]
    .sort(
      (a, b) =>
        a.originalIndex -
        b.originalIndex
    )
    .forEach(
      (source) => {
        usedIds.add(
          source.id
        );

        normalized.push(
          source
        );
      }
    );

  /*
   * Pass 2:
   * Sources without a valid explicit citation ID receive a unique generated ID.
   * Existing valid IDs are never renumbered.
   */
  pending.forEach(
    (source) => {
      const id =
        nextAvailableCitationId(
          usedIds,
          source.originalIndex +
            1
        );

      if (id === null) {
        return;
      }

      usedIds.add(id);

      normalized.push({
        ...source,
        id,
      });
    }
  );

  /*
   * IMPORTANT:
   * Do NOT dedupe by URL alone.
   *
   * [2] and [5] may legitimately point to the same URL. Removing either row
   * would break the visible source-number contract used by inline citations.
   */
  normalized.sort(
    (a, b) =>
      a.id - b.id
  );

  return normalized.map(
    ({
      originalIndex,
      ...source
    }) => source
  );
}

function sourceSignature(
  sources
) {
  return JSON.stringify(
    (sources || []).map(
      (source) => [
        source.id,
        source.label,
        source.url,
      ]
    )
  );
}

export default function AnswerSources({
  sources = [],
  batchSize = 5,
}) {
  const safeBatchSize =
    normalizeBatchSize(
      batchSize
    );

  const cleanSources =
    useMemo(
      () =>
        normalizeSources(
          sources
        ),
      [sources]
    );

  const signature =
    useMemo(
      () =>
        sourceSignature(
          cleanSources
        ),
      [cleanSources]
    );

  const [
    visibleCount,
    setVisibleCount,
  ] =
    useState(
      safeBatchSize
    );

  /*
   * A different answer/source set starts at the first batch again.
   * "Show more" state should not leak from one answer into another.
   */
  useEffect(() => {
    setVisibleCount(
      safeBatchSize
    );
  }, [
    signature,
    safeBatchSize,
  ]);

  if (
    !cleanSources.length
  ) {
    return null;
  }

  const visibleSources =
    cleanSources.slice(
      0,
      visibleCount
    );

  const remaining =
    Math.max(
      0,
      cleanSources.length -
        visibleCount
    );

  const nextAmount =
    Math.min(
      safeBatchSize,
      remaining
    );

  return (
    <div className="answer-sources">
      <div className="answer-sources-title">
        Sources
      </div>

      <div className="answer-sources-list">
        {visibleSources.map(
          (source) => {
            const content = (
              <>
                <span className="answer-source-number">
                  [{source.id}]
                </span>

                <span className="answer-source-label">
                  {source.label}
                </span>
              </>
            );

            return (
              <div
                key={`source-${source.id}`}
                className="answer-source-row"
              >
                {source.url ? (
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    referrerPolicy="no-referrer"
                    className="answer-source-link"
                    aria-label={`Open source ${source.id}: ${source.label}`}
                    title={`Open source ${source.id}: ${source.label}`}
                  >
                    {content}
                  </a>
                ) : (
                  <span
                    className="answer-source-link answer-source-link-static"
                    aria-label={`Source ${source.id}: ${source.label}`}
                  >
                    {content}
                  </span>
                )}
              </div>
            );
          }
        )}
      </div>

      {remaining > 0 ? (
        <button
          type="button"
          className="answer-show-more-sources"
          onClick={() =>
            setVisibleCount(
              (prev) =>
                Math.min(
                  cleanSources.length,
                  prev +
                    safeBatchSize
                )
            )
          }
        >
          Show {nextAmount} more
        </button>
      ) : null}
    </div>
  );
}
